import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Stack,
  Switch,
  Text,
  VStack,
  Tooltip,
  Icon,
  Spinner,
} from "@chakra-ui/react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import CurrencyInput from "react-currency-input-field";
import { FaTrashAlt } from "react-icons/fa";
import { FiPlus, FiInfo } from "react-icons/fi";
import InputWithLabel from "../InputWithLabel";
import { TicketTypeTier, TicketTypeValues } from "../schema";
import { useCallback, useState, useEffect } from "react";
import DatepickerFormInput from "../../DatepickerFormInput";
import actions from "../../../../../actions";
import { dineroFormatCurrency } from "src/utils";

const TIER_LIMIT = 9;

const TieredAllocation = ({
  currency,
  loading = false,
  timezone,
  eventId,
  onRemoveTier,
}: {
  currency: string;
  loading: boolean;
  timezone: string;
  eventId: string;
  onRemoveTier: (tier: TicketTypeTier) => Promise<void>;
}) => {
  const {
    register,
    control,
    setValue,
    watch,
    trigger,
    formState: {
      errors,
      defaultValues,
      touchedFields,
      dirtyFields,
      isSubmitted,
    },
  } = useFormContext<TicketTypeValues>();
  const { append, remove } = useFieldArray({
    control,
    keyName: "field_id",
    name: "tiers",
  });

  const currentTiers = watch("tiers");
  const [isTierCodesOpen, setIsTierCodesOpen] = useState(
    currentTiers.reduce((tiersIsOpen, tier) => {
      return {
        ...tiersIsOpen,
        [tier.id]: tier.code ? true : tier.hidden,
      };
    }, {})
  );

  const [tierPriceBreakdowns, setTierPriceBreakdowns] = useState<
    Record<
      number,
      {
        service_fee: number;
        processing_fee: number;
        rebate_fee: number;
        final_fees: number;
        final_price: number;
      }
    >
  >({});
  const [isCalculating, setIsCalculating] = useState(false);

  const tiers = currentTiers.filter((t) => t.status !== "DELETED");

  // Calculate prices for all tiers
  useEffect(() => {
    setIsCalculating(true);
    const calculateTierPrices = async () => {
      const breakdowns: typeof tierPriceBreakdowns = {};

      for (let i = 0; i < tiers.length; i++) {
        const tierPrice = watch(`tiers.${i}.price`);
        if (tierPrice && !isNaN(parseFloat(tierPrice))) {
          try {
            const { total_price, total_fees, ticket_fees } =
              await actions.ADMIN.calculateFinalTicketPrice(
                parseFloat(tierPrice),
                eventId
              );
            breakdowns[i] = {
              service_fee:
                ticket_fees.find((fee) => fee.type === "service_fee")?.price
                  .amount || 0,
              processing_fee:
                ticket_fees.find((fee) => fee.type === "processing_fee")?.price
                  .amount || 0,
              rebate_fee:
                ticket_fees.find((fee) => fee.type === "rebate")?.price
                  .amount || 0,
              final_fees: total_fees?.getAmount() || 0,
              final_price: total_price?.getAmount() || 0,
            };
          } catch (error) {
            console.error(`Error calculating price for tier ${i}:`, error);
          }
        }
      }
      setIsCalculating(false);

      setTierPriceBreakdowns(breakdowns);
    };

    const timeoutId = setTimeout(calculateTierPrices, 300);
    return () => clearTimeout(timeoutId);
  }, [tiers.map((t) => t.price).join(","), eventId]);

  const updateTierCodeOpen = useCallback(
    (tierId: string, isOpen: boolean) => {
      setIsTierCodesOpen((isTierCodesOpen) => {
        return {
          ...isTierCodesOpen,
          [tierId]: isOpen,
        };
      });
    },
    [setIsTierCodesOpen]
  );

  const isEditIndex = (index) => watch(`tiers.${index}.id`);
  const isEdit = !!watch("id");

  const hasError = (index, prop) => {
    if (!errors["tiers"]) return false;

    if (!errors["tiers"][index]) return false;

    return prop in errors["tiers"][index];
  };

  const getErrorFor = (index: number, prop: string) => {
    if (!errors["tiers"]) return undefined;

    if (index in errors["tiers"]) {
      if (errors["tiers"][index]) {
        if (prop in errors["tiers"][index]) {
          return errors["tiers"][index][prop].message;
        }
        return;
      }
      return;
    }
    return;
  };

  const movesTicketsToNextBucket = watch("shift_quantity_on_expiration");

  const tierCanBeDeleted = (tier, index) => {
    return (
      !isEdit ||
      tier.isNew ||
      (tier.display_options?.delete_options?.includes("DELETE") && index !== 0)
    );
  };

  return (
    <>
      {tiers.length ? (
        <HStack w="100%" mb={1} gap={0}>
          <Text w={isEdit ? "25%" : "20%"}>Name</Text>
          <Text w={isEdit ? "18%" : "16%"}>Quantity</Text>
          <Text w={isEdit ? "23%" : "22%"}>Price</Text>
          <Text>
            Expires at {movesTicketsToNextBucket ? "(*)" : "(Optional)"}
          </Text>
        </HStack>
      ) : null}
      <VStack w="full" spacing={4}>
        {tiers.map((t, index) => (
          <VStack
            alignItems={"flex-start"}
            key={t.field_id}
            borderBottomWidth={1}
            borderBottomColor={"#ddd"}
            paddingBottom={4}
          >
            <HStack w="100%" key={t.id} alignItems={"flex-start"}>
              <VStack spacing={1} w={isEdit ? "25%" : "21%"}>
                <HStack w="full" spacing={2} alignItems="center">
                  <Input
                    flex={1}
                    height="48px"
                    required
                    placeholder="Name"
                    isInvalid={hasError(index, "name")}
                    {...register(`tiers.${index}.name`, {
                      onChange: () => trigger(`tiers.${index}.name`),
                      disabled: loading,
                    })}
                  />
                </HStack>
                <Box h={4} w="full">
                  {hasError(index, "name") ? (
                    <Text
                      fontSize={"xs"}
                      color="red.300"
                      w="full"
                      lineHeight={4}
                    >
                      {`${getErrorFor(index, "name")}`}
                    </Text>
                  ) : null}
                </Box>
              </VStack>
              <VStack spacing={1} w="17%">
                <Input
                  height="48px"
                  required
                  inputMode="numeric"
                  type="number"
                  placeholder="Qty"
                  disabled={loading}
                  isInvalid={hasError(index, "quantity")}
                  min={
                    isEditIndex(index)
                      ? defaultValues?.tiers[index]?.quantity
                      : 1
                  }
                  {...register(`tiers.${index}.quantity`, {
                    min: isEditIndex(index)
                      ? defaultValues?.tiers[index]?.quantity
                      : 1,
                    setValueAs: (v) => {
                      if (v === "" || v === null || typeof v === "undefined") {
                        return undefined;
                      }
                      const n = parseInt(String(v), 10);
                      return Number.isNaN(n) ? undefined : n;
                    },
                  })}
                />
                <Box h={4} w="full">
                  {hasError(index, "quantity") ? (
                    <Text fontSize={"xs"} color="red.300" lineHeight={4}>
                      {`${getErrorFor(index, "quantity")}`}
                    </Text>
                  ) : null}
                </Box>
              </VStack>

              <Controller
                control={control}
                name={`tiers.${index}.price`}
                render={({ field }) => (
                  <VStack spacing={1} w="24%">
                    <CurrencyInput
                      //@ts-ignore
                      style={{ width: "100%" }}
                      //@ts-ignore
                      borderColor={hasError(index, "price") ? "red" : "inherit"}
                      //@ts-ignore
                      borderWidth={hasError(index, "price") ? 2 : 1}
                      height="48px"
                      placeholder="Price"
                      required
                      value={field.value}
                      disabled={loading}
                      onValueChange={(value) => {
                        setValue(`tiers.${index}.price`, value || "", {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        trigger(`tiers.${index}.price`);
                      }}
                      onBlur={() => {
                        field.onBlur();
                        trigger(`tiers.${index}.price`);
                      }}
                      intlConfig={{
                        locale: "en-US",
                        currency,
                      }}
                      customInput={Input}
                      step={1}
                    />
                    <Box h={4} w="full">
                      {hasError(index, "price") ? (
                        <Text fontSize={"xs"} color="red.300" lineHeight={4}>
                          {`${getErrorFor(index, "price")}`}
                        </Text>
                      ) : null}
                    </Box>
                  </VStack>
                )}
              />
              <VStack minW={"34%"} flex={1}>
                <DatepickerFormInput
                  skipValidate
                  method="onChange"
                  overrideError={getErrorFor(index, "expires_at")}
                  deps={[`tiers.${index + 1}`, `tiers.${index - 1}`]}
                  name={`tiers.${index}.expires_at`}
                  timezone={timezone}
                  dateTimeProps={{
                    disabled: loading,
                    disablePast: true,
                    slotProps: {
                      actionBar: {
                        actions: ["clear", "accept"],
                      },
                    },
                    sx: {
                      "& .MuiInputBase-input": {
                        padding: "8px",
                      },
                      ...(hasError(index, "expires_at") && {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "red",
                          borderWidth: "2px",
                        },
                      }),
                    },
                  }}
                />
              </VStack>
              {tierCanBeDeleted(t, index) ? (
                <IconButton
                  w="9%"
                  isDisabled={loading}
                  aria-label={`Delete tier ${index}`}
                  variant={"ghost"}
                  onClick={() => {
                    if (t.id.startsWith("_CREATE_")) {
                      remove(index);
                    } else {
                      onRemoveTier(t);
                    }
                  }}
                  icon={<FaTrashAlt fontSize={"1.0rem"} />}
                />
              ) : null}
            </HStack>
            <HStack mt="-20px">
              {t.sold_out ? (
                <Badge colorScheme="orange" flexShrink={0}>
                  Sold out
                </Badge>
              ) : null}
              {!hasError(index, "quantity") && t.sold_out
                ? (() => {
                    const current = watch(`tiers.${index}.quantity`);
                    const original = defaultValues?.tiers?.[index]?.quantity;

                    const currentQty =
                      typeof current === "number" ? current : NaN;
                    const originalQty =
                      typeof original === "number" ? original : NaN;

                    if (Number.isNaN(currentQty) || Number.isNaN(originalQty))
                      return null;

                    const delta = Math.max(0, currentQty - originalQty);
                    if (delta <= 0) return null;
                    return (
                      <Text fontSize="xs" color="gray.600" lineHeight={4}>
                        Adding {delta} tickets will return this ticket type to{" "}
                        <Badge colorScheme="green">On Sale</Badge> status.
                      </Text>
                    );
                  })()
                : (() => {
                    const current = watch(`tiers.${index}.quantity`);
                    const willBeSoldOut =
                      typeof current === "number" &&
                      typeof t.totalSold === "number" &&
                      current === t.totalSold;
                    return willBeSoldOut ? (
                      <Text fontSize="xs" color="gray.600" lineHeight={4}>
                        This will move this ticket type to the{" "}
                        <Badge colorScheme="orange">Sold out</Badge> status.
                      </Text>
                    ) : null;
                  })()}
            </HStack>
            <VStack
              w="full"
              justifyContent={"space-between"}
              alignItems={"flex-start"}
            >
              <Accordion w="full" allowMultiple>
                <AccordionItem w="full" border="none">
                  <h2>
                    <AccordionButton pl="0px">
                      <Box
                        fontSize="xs"
                        fontWeight={"medium"}
                        color="gray.600"
                        as="span"
                        textAlign="left"
                      >
                        Tier Settings
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <Stack w="full">
                      <HStack>
                        <Stack minW={"250px"} w="50%" spacing={0}>
                          <Text>On Sale Date</Text>
                          <DatepickerFormInput
                            method="onChange"
                            overrideError={getErrorFor(index, "on_sale_at")}
                            deps={[
                              `tiers.${index + 1}`,
                              `tiers.${index - 1}`,
                              `tiers.${index}.off_sale_at`,
                            ]}
                            name={`tiers.${index}.on_sale_at`}
                            timezone={timezone}
                            dateTimeProps={{
                              disabled: loading,
                              disablePast: true,
                              slotProps: {
                                actionBar: {
                                  actions: ["clear", "accept"],
                                },
                              },
                              sx: {
                                "& .MuiInputBase-input": {
                                  padding: "8px",
                                },
                              },
                            }}
                          />
                        </Stack>
                        <Stack minW={"250px"} w="50%" spacing={0}>
                          <Text>Off Sale Date</Text>
                          <DatepickerFormInput
                            method="onChange"
                            overrideError={getErrorFor(index, "off_sale_at")}
                            deps={[
                              `tiers.${index + 1}`,
                              `tiers.${index - 1}`,
                              `tiers.${index}.on_sale_at`,
                            ]}
                            name={`tiers.${index}.off_sale_at`}
                            timezone={timezone}
                            dateTimeProps={{
                              disabled: loading,
                              disablePast: true,
                              slotProps: {
                                actionBar: {
                                  actions: ["clear", "accept"],
                                },
                              },
                              sx: {
                                "& .MuiInputBase-input": {
                                  padding: "8px",
                                },
                              },
                            }}
                          />
                        </Stack>
                      </HStack>
                      <Stack
                        spacing={0}
                        justifyContent={"space-between"}
                        w="full"
                      >
                        <Text>Hidden</Text>
                        <HStack gap={4}>
                          <Switch
                            colorScheme="green"
                            defaultChecked={!!t.code || t.hidden}
                            isDisabled={loading || !!t.code}
                            onChange={() => {
                              const changingTo = !isTierCodesOpen[t.id];

                              setValue(`tiers.${index}.hidden`, changingTo, {
                                shouldDirty: true,
                              });
                              updateTierCodeOpen(t.id, changingTo);
                            }}
                            ml={2}
                          />
                          <Text fontWeight={"medium"}>
                            Require code to unlock
                          </Text>
                        </HStack>
                      </Stack>
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </VStack>
          </VStack>
        ))}
        {tiers.length <= TIER_LIMIT ? (
          <Button
            w="full"
            leftIcon={<FiPlus />}
            isDisabled={loading}
            onClick={() => {
              append({
                isNew: true,
                id: `_CREATE_${tiers.length}_`,
                name: "",
                price: "",
                quantity: "",
                code: null,
                expires_at: null,
              });
            }}
          >
            Add Tier
          </Button>
        ) : null}
        <HStack w="100%">
          <Stack w="40%">
            <Text
              fontSize="sm"
              color="gray.500"
              textAlign="right"
              top="30px"
              mt="-30px"
              position="relative"
            >
              (Max 100)
            </Text>
            <InputWithLabel
              name="limit"
              required
              inputProps={{
                disabled: loading,
              }}
              label="Limit"
              placeholder="Limit"
            ></InputWithLabel>
          </Stack>
        </HStack>
        {isCalculating ? (
          <HStack
            w="full"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            bg="gray.50"
            p={4}
            borderRadius="md"
          >
            <Spinner size="sm" color="gray.400" thickness="2px" />
            <Text fontSize="xs" color="gray.500">
              Loading price breakdown...
            </Text>
          </HStack>
        ) : (
          Object.keys(tierPriceBreakdowns).length > 0 && (
            <VStack w="full" p={4} bg="gray.50" borderRadius="md">
              <HStack
                w="full"
                fontWeight="medium"
                fontSize="sm"
                color="gray.600"
              >
                <Text flex={1}>Tier name</Text>
                <Text w="15%" align="right">
                  Price
                </Text>
                <Text w="15%" align="right">
                  Fees
                </Text>
                <Text w="15%" align="right">
                  Fan Price
                </Text>
              </HStack>
              {tiers.map((tier, index) => {
                const breakdown = tierPriceBreakdowns[index];
                if (!breakdown) return null;

                return (
                  <HStack key={tier.id} w="full" fontSize="sm" color="gray.700">
                    <Text flex={1}>{tier.name || `Tier ${index + 1}`}</Text>
                    <Text w="15%" align="right">
                      {dineroFormatCurrency(
                        parseFloat(tier.price || "0") * 100
                      )}
                    </Text>
                    <Tooltip
                      label={
                        <>
                          {breakdown?.service_fee > 0 && (
                            <>
                              Service fee:{" "}
                              {dineroFormatCurrency(breakdown?.service_fee)}
                            </>
                          )}
                          {breakdown?.processing_fee > 0 && (
                            <>
                              <br />
                              Processing fee:{" "}
                              {dineroFormatCurrency(breakdown?.processing_fee)}
                            </>
                          )}
                          {breakdown?.rebate_fee > 0 && (
                            <>
                              <br />
                              Rebate Fee:{" "}
                              {dineroFormatCurrency(breakdown?.rebate_fee)}
                            </>
                          )}
                        </>
                      }
                      placement="bottom"
                    >
                      <Text w="15%" align="right" cursor="help">
                        {dineroFormatCurrency(breakdown?.final_fees)}{" "}
                        <span>
                          <Icon as={FiInfo} boxSize={3} color="gray.500" />
                        </span>
                      </Text>
                    </Tooltip>
                    <Text w="15%" align="right">
                      {dineroFormatCurrency(breakdown.final_price)}
                    </Text>
                  </HStack>
                );
              })}
            </VStack>
          )
        )}
      </VStack>
    </>
  );
};

export default TieredAllocation;
